// @ts-check
import { gql, useMutation, useQuery } from '@apollo/client';
import { Trans } from '@lingui/macro';
import React from 'react';
import { Box, TabbedMenu, TabbedMenuItem } from '~/design-system';
import getSuppliers from '../MatchSupplier/getSuppliers';
import getSuppliersMatched from '../MatchSupplier/getSuppliersMatched';
import Notification from '../Notification';
import SuppliersOverviewSkeleton from './SuppliersOverviewSkeleton';
import SuppliersTable from './SuppliersTable';

/**
 * The props that this component needs.
 * @typedef {object} MatchSuppliersStepProps
 * @property {string} workspaceId
 * @property {string} importId
 * @property {string} tab
 * @property {object} link
 */

/**
 * Renders the suppliers page overview, with the list of matched and unmatched suppliers
 * @param {ReturnType<useMatchSuppliers> & MatchSuppliersStepProps} props
 * @returns
 */
export default function SuppliersOverview({
  selectedSupplier,
  setSelectedSupplier,
  onSelectSupplier,
  allSuppliers,
  matchedSuppliers,
  unmatchedSuppliers,
  suppliersMap,
  importToolPartners,
  tab,
  notification,
  cancelNotification,
  loading,
  link,
  ...rest
}) {
  if (loading) return <SuppliersOverviewSkeleton {...rest} />;
  return (
    <Box {...rest}>
      <TabbedMenu sx={{ margin: 0 }}>
        <TabbedMenuItem
          isActive={tab === 'all'}
          to={link._all.suppliers({ tab: 'all' })}
        >
          <Trans>all</Trans> (
          {matchedSuppliers && unmatchedSuppliers
            ? matchedSuppliers?.length + unmatchedSuppliers?.length
            : 0}
          )
        </TabbedMenuItem>
        <TabbedMenuItem
          isActive={tab === 'unmatched'}
          to={link._all.suppliers({ tab: 'unmatched' })}
        >
          <Trans>unmatched</Trans> ({unmatchedSuppliers?.length})
        </TabbedMenuItem>
        <TabbedMenuItem
          isActive={tab === 'matched'}
          to={link._all.suppliers({ tab: 'matched' })}
        >
          <Trans>matched</Trans> ({matchedSuppliers?.length})
        </TabbedMenuItem>
      </TabbedMenu>

      <SuppliersTable
        suppliers={
          tab === 'all'
            ? allSuppliers
            : tab === 'unmatched'
            ? unmatchedSuppliers
            : matchedSuppliers
        }
        selectedSupplier={selectedSupplier}
        suppliersMap={suppliersMap}
        onSelectSupplier={(supplier) => {
          setSelectedSupplier(supplier);
          onSelectSupplier(supplier);
        }}
        importToolPartners={importToolPartners}
      />
      {notification && (
        <Notification
          onClose={() => cancelNotification()}
          message={notification.message}
          variant={notification.success ? 'success' : 'error'}
        />
      )}
    </Box>
  );
}

function supplierSorter(a, b) {
  if (a < b) {
    return -1;
  }
  if (a > b) {
    return 1;
  }
  return 0;
}
/**
 * The business logic. The route is then responsible for wiring up the returned props to the view component.
 * @param {MatchSuppliersStepProps} props
 * @returns
 */
export function useMatchSuppliers({ workspaceId, importId }) {
  const { data, loading, refetch } = useQuery(QUERY, {
    variables: { workspaceId, importId },
  });
  const [updateSuppliersMapping] = useMutation(UPDATE_SUPPLIERS_MAPPINGS);

  /** @type {[import('../Notification').Notification | undefined, React.Dispatch<React.SetStateAction<import('../Notification').Notification | undefined>>]} */
  const [notification, setNotification] = React.useState();
  const [isSubmiting, setIsSubmiting] = React.useState(false);
  const importData = data?.workspace?.dataImport || {};

  const [selectedSupplier, setSelectedSupplier] = React.useState(false);

  if (loading)
    return {
      loading,
    };

  const savedSuppliers = importData?.suppliers; //the existing map, saved for the current import.
  const allSuppliers = getSuppliers({ importData });
  const importToolPartners = data?.workspace?.importToolPartners?.nodes;
  const previousMatches = getSuppliersMatched({
    importData,
    partners: importToolPartners,
  });
  const suppliersMap = previousMatches?.reduce((acc, supplier) => {
    if (allSuppliers.includes(supplier.supplier)) {
      acc[supplier.supplier] = supplier.partnerId;
    }
    return acc;
  }, {});
  const matchedSuppliers = allSuppliers
    .filter((supplier) => suppliersMap?.[supplier])
    ?.sort(supplierSorter);
  const unmatchedSuppliers = allSuppliers
    .filter((supplier) => !suppliersMap?.[supplier])
    ?.sort(supplierSorter);

  const newMapping = previousMatches?.reduce((acc, match) => {
    acc[match.supplier] = match;
    return acc;
  }, {});

  async function saveSuppliersMatch() {
    if (isSubmiting) return;
    setIsSubmiting(true);
    const suppliers = Object.values(newMapping).map(
      ({ partnerId, supplier }) => {
        return { partnerId, supplier };
      }
    );
    const { data } = await updateSuppliersMapping({
      variables: {
        importId,
        suppliers,
      },
    });
    setIsSubmiting(false);
    if (!data?.result?.success) {
      return setNotification({
        message: 'There was an error, please try again!',
      });
    }
    return data;
  }

  return {
    loading,
    refetch,
    importId,
    selectedSupplier,
    setSelectedSupplier,
    allSuppliers,
    matchedSuppliers,
    unmatchedSuppliers,
    suppliersMap,
    savedSuppliers,
    importToolPartners,
    saveSuppliersMatch,
    notification,
    cancelNotification: () => {
      setNotification(undefined);
    },
    isValid: unmatchedSuppliers?.length === 0 && !isSubmiting,
  };
}

SuppliersOverview.fragment = gql`
  fragment SuppliersOverview on DataImport {
    importId
    type
    sourceFields {
      name
    }
    suppliers {
      supplier
      partnerId
    }
    allSuppliers: rows(
      filter: {
        # Gets all suppliers (includes duplicates)
        # The filter is a bit complex to keep it in sync with sift.
        # It means:
        # Return any row that has a matching element in the values array with:
        # - the bottlebooksFieldName set to "exhibitor.name"
        values: {
          elemMatch: { bottlebooksFieldName: { eq: "exhibitor.name" } }
        }
      }
    ) {
      nodes {
        supplierName: bottlebooksFieldValue(name: "exhibitor.name") {
          rawText
        }
      }
    }
    allProducers: rows {
      nodes {
        producerName: bottlebooksFieldValue(name: "producer.name") {
          rawText
        }
      }
    }
  }
`;

const QUERY = gql`
  query MapSuppliers($workspaceId: ID!, $importId: ID!) {
    workspace(workspaceId: $workspaceId) {
      workspaceId
      dataImport(importId: $importId) {
        ...SuppliersOverview
      }
      importToolPartners {
        nodes {
          partnerId
          companyId
          aliases
          brand {
            brandId
            name
            logo {
              fixed(height: 80, width: 80, crop: PAD, fetch_format: AUTO) {
                src
                srcSet
              }
            }
          }
        }
      }
    }
  }
  ${SuppliersOverview.fragment}
`;

export const UPDATE_SUPPLIERS_MAPPINGS = gql`
  mutation updateDataImportSuppliersMappings(
    $importId: ID!
    $suppliers: [DataImportSuppliersMappingInput!]!
  ) {
    result: updateDataImportSuppliersMappings(
      input: { importId: $importId, suppliers: $suppliers }
    ) {
      code
      success
      message
      dataImport {
        importId
        suppliers {
          supplier
          partnerId
        }
      }
    }
  }
`;
